import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  SelectComponent,
} from '@verticalbridge/ui-components';
import { AuthService } from '@auth0/auth0-angular';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { ColoUser, RequestAccessService } from '../../core/services/request-access.service';

const US_STATES: { label: string; value: string }[] = [
  { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' }, { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' },
  { label: 'District of Columbia', value: 'DC' }, { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' }, { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' }, { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' }, { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' }, { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' }, { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' }, { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' }, { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

const AFFILIATIONS = [
  { label: 'Wireless Operator', value: 'Wireless Operator' },
  { label: 'Broadcaster (AM/FM, TV)', value: 'Broadcaster (AM/FM, TV)' },
  {
    label: 'Other Wireless Tenant (WISP, Government, IOT, etc.)',
    value: 'Other Wireless Tenant (WISP, Government, IOT, etc.)',
  },
  { label: 'Site Acquisition Professional', value: 'Site Acquisition Professional' },
  { label: 'Other', value: 'Other' },
];

const ROLES = [
  { label: 'Construction', value: 'Construction' },
  { label: 'Field Operations', value: 'Field Operations' },
  { label: 'Network Operations', value: 'Network Operations' },
  { label: 'Real Estate', value: 'Real Estate' },
  { label: 'RF Engineering', value: 'RF Engineering' },
  { label: 'Site Acquisition', value: 'Site Acquisition' },
  { label: 'Site Development', value: 'Site Development' },
  { label: 'Other', value: 'Other' },
];

@Component({
  selector: 'app-register',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    SelectComponent,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {
  private fb = inject(NonNullableFormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(PortalAuthService);
  private auth0 = inject(AuthService);
  private requestAccessService = inject(RequestAccessService);
  private destroyRef = inject(DestroyRef);

  protected readonly states = US_STATES;
  protected readonly affiliations = AFFILIATIONS;
  protected readonly roles = ROLES;

  protected isSaving = signal(false);
  protected errorMessage = signal<string | null>(null);

  protected form = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    title: ['', [Validators.required]],
    company: ['', [Validators.required]],
    address1: ['', [Validators.required]],
    address2: [''],
    city: ['', [Validators.required]],
    state: ['', [Validators.required]],
    postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^[\d\s()+.-]{10,}$/)]],
    describesAffiliationWithVB: ['', [Validators.required]],
    describesRoleInCompany: ['', [Validators.required]],
    iAgree: [false, [Validators.requiredTrue]],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  protected readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.isSaving());

  constructor() {
    const jwtParts = (this.authService.getUserInfo()?.name
      ?? this.authService.getUserInfo()?.nickname
      ?? '').split(' ');
    const jwtFirst = jwtParts[0] ?? '';
    const jwtLast = jwtParts.slice(1).join(' ');

    this.requestAccessService.getColoUserData().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: existing => {
        if (existing) {
          const current = this.form.getRawValue();
          this.form.patchValue({
            firstName: current.firstName || existing.firstName || jwtFirst,
            lastName: current.lastName || existing.lastName || jwtLast,
            title: current.title || existing.title || '',
            company: current.company || existing.company || '',
            address1: current.address1 || existing.address1 || '',
            address2: current.address2 || existing.address2 || '',
            city: current.city || existing.city || '',
            state: current.state || existing.state || '',
            postalCode: current.postalCode || existing.postalCode || '',
            phoneNumber: current.phoneNumber || existing.phoneNumber || '',
            describesAffiliationWithVB: current.describesAffiliationWithVB || existing.describesAffiliationWithVB || '',
            describesRoleInCompany: current.describesRoleInCompany || existing.describesRoleInCompany || '',
          });
        } else {
          this.form.patchValue({
            firstName: this.form.controls.firstName.value || jwtFirst,
            lastName: this.form.controls.lastName.value || jwtLast,
          });
        }
      },
      error: () => {
        this.form.patchValue({
          firstName: this.form.controls.firstName.value || jwtFirst,
          lastName: this.form.controls.lastName.value || jwtLast,
        });
      },
    });

    const applyName = (rawName: string | null | undefined) => {
      const fullName = rawName ?? '';
      const parts = fullName.split(' ');
      const first = parts[0] ?? '';
      const last = parts.slice(1).join(' ');
      const current = this.form.getRawValue();
      this.form.patchValue({
        firstName: current.firstName || first,
        lastName: current.lastName || last,
      });
    };

    this.authService.userProfile$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(profile => {
      if (!profile) return;
      applyName(profile.name || profile.nickname);
    });

    this.auth0.user$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(user => {
      if (!user) return;
      applyName(user.name || user.nickname || user.given_name);
    });
  }

  protected onCancel(): void {
    this.router.navigate(['/home']);
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.errorMessage.set('Please fill in all required fields.');
      return;
    }

    const userEmail = this.authService.getUserInfo()?.email ?? '';
    if (!userEmail) {
      this.errorMessage.set('We could not read your email from your session. Please sign out and back in.');
      return;
    }

    const v = this.form.getRawValue();
    const payload: ColoUser = {
      id: userEmail,
      userEmail,
      firstName: v.firstName,
      lastName: v.lastName,
      title: v.title,
      company: v.company,
      address1: v.address1,
      address2: v.address2,
      city: v.city,
      state: v.state,
      postalCode: v.postalCode,
      phoneNumber: v.phoneNumber,
      describesAffiliationWithVB: v.describesAffiliationWithVB,
      describesRoleInCompany: v.describesRoleInCompany,
    };

    this.isSaving.set(true);
    this.errorMessage.set(null);

    this.requestAccessService.ensureColoUser(payload).pipe(
      switchMap(() =>
        this.authService.refreshToken().pipe(catchError(() => of(null))),
      ),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.isSaving.set(false);
        const returnApp = this.route.snapshot.queryParamMap.get('app');
        if (returnApp === 'colo') {
          window.location.href = this.requestAccessService.buildColoHandoffUrl();
          return;
        }
        this.router.navigate(['/home']);
      },
      error: (err) => {
        this.isSaving.set(false);
        this.errorMessage.set(
          err?.error?.message
            ?? 'Failed to save registration. Please try again.',
        );
      },
    });
  }
}
